import { UserObject } from '../helpers/objects/userObject';
import styles from '../css/users.module.scss';
import { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import { EnvPaths, freezeScroll, unfreezeScroll } from '../helpers/environment';
import CustomTextInput from './customTextInput';
import { useAuth } from '../context/AuthContext';
import CustomDropdown from './customDropdown';
import { useTranslation } from 'react-i18next';

const EditUserModal = ({ user, isOpen, setIsOpen, refetch, setRefetch }: { user: UserObject; isOpen: boolean; setIsOpen: (isOpen: boolean) => void; refetch: boolean; setRefetch: (refetch: boolean) => void }) => {
  const [editMode, setEditMode] = useState(false);
  const [editUserObject, setEditUserObject] = useState<UserObject>(user);
  const [loading, setLoading] = useState(false);
  const admin = useAuth().user;
  const { t } = useTranslation();
  const userRoleOptions = [
    { value: 'user', label: t('editUserModal.user') },
    { value: 'admin', label: t('editUserModal.admin') },
    { value: 'skycop', label: t('editUserModal.skycop') },
  ];
  const [isRoleSelectOpen, setIsRoleSelectOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setEditUserObject(user);
    }
  }, [user, isOpen]);

  const handleUpdate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    axios
      .post(
        `${EnvPaths.SERVER_URL}/admin/update-user`,
        {
          updates: editUserObject,
          userId: user._id,
        },
        {
          headers: {
            Authorization: `Bearer ${admin?.token}`,
          },
        }
      )
      .then(() => {
        toast.dismiss();
        toast.success(t('editUserModal.handleUpdateToast.success'));
        setLoading(false);
        setIsOpen(false);
        setEditMode(false);
        setRefetch(!refetch);
      })
      .catch((error) => {
        console.error(error);
        toast.dismiss();
        toast.error(t('editUserModal.handleUpdateToast.error'));
        setLoading(false);
      });
  };

  const handleDisableUser = () => {
    setLoading(true);

    axios
      .post(
        `${EnvPaths.SERVER_URL}/admin/disable-user`,
        {
          userId: user._id,
        },
        {
          headers: {
            Authorization: `Bearer ${admin?.token}`,
          },
        }
      )
      .then(() => {
        toast.dismiss();
        toast.success(t('editUserModal.handleDisableToast.success'));
        setEditUserObject({ ...user, isBlocked: true });
        setLoading(false);
        setIsOpen(false);
        setRefetch(!refetch);
      })
      .catch((error) => {
        console.error(error);
        toast.dismiss();
        toast.error(t('editUserModal.handleDisableToast.error'));
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isOpen) {
      freezeScroll();
    } else {
      unfreezeScroll();
    }
  }, [isOpen]);

  const handleEnableUser = () => {
    setLoading(true);

    axios
      .post(
        `${EnvPaths.SERVER_URL}/admin/enable-user`,
        {
          userId: user._id,
        },
        {
          headers: {
            Authorization: `Bearer ${admin?.token}`,
          },
        }
      )
      .then(() => {
        toast.dismiss();
        toast.success(t('editUserModal.handleEnableToast.success'));
        setEditUserObject({ ...user, isBlocked: false });
        setLoading(false);
        setIsOpen(false);
        setRefetch(!refetch);
      })
      .catch((error) => {
        console.error(error);
        toast.dismiss();
        toast.error(t('editUserModal.handleEnableToast.error'));
        setLoading(false);
      });
  };

  const handleRoleChange = async (role: 'user' | 'admin' | 'skycop') => {
    setLoading(true);

    axios
      .patch(
        `${EnvPaths.SERVER_URL}/admin/update-user-role`,
        { userId: user._id, role },
        {
          headers: { Authorization: `Bearer ${admin?.token}` },
        }
      )
      .then(() => {
        toast.success(t('editUserModal.handleRoleChangeToast.success'));
        setLoading(false);
        setRefetch(!refetch);
      })
      .catch((error) => {
        console.error(error);
        toast.error(t('editUserModal.handleRoleChangeToast.error'));
        setLoading(false);
      });
  };

  return (
    <div className={`${styles.editUserModalContainer} ${isOpen ? styles.open : ''}`}>
      <form onSubmit={handleUpdate} className={`${styles.editUserModalContent}`}>
        <button
          type="button"
          disabled={loading}
          onClick={() => {
            setEditMode(false);
            setIsOpen(false);
          }}
          className={`${styles.closeButton}`}
        >
          <img src="/assets/images/close-icon.svg" alt="Close" />
        </button>
        <h2>
          {t('editUserModal.editUser', {
            userName: user?.firstName && user?.lastName ? `${user?.firstName} ${user?.lastName}` : user?.email,
          })}
        </h2>
        <hr />
        <div className={styles.editUserInfoSection}>
          <div className={styles.popoverContainer}>
            <button type="button" onClick={() => (!user?.isBlocked ? handleDisableUser() : handleEnableUser())} className={`primaryButton ${!user?.isBlocked ? 'danger' : ''}`}>
              <img src={`/assets/images/exclamation-mark-outlined${!user?.isBlocked ? '-danger' : ''}.svg`} alt="Delete" />
              {!user?.isBlocked ? t('editUserModal.block') : t('editUserModal.unblock')} {t('editUserModal.user')}
            </button>
          </div>
          <CustomDropdown
            width="100%"
            isOpen={isRoleSelectOpen}
            onToggle={() => setIsRoleSelectOpen((prev) => !prev)}
            value={userRoleOptions.find((opt) => opt.value === editUserObject?.role) ?? { value: 'user', label: 'User' }}
            onChange={(e) => {
              setEditUserObject({
                ...editUserObject,
                role: e.value as 'user' | 'admin' | 'skycop',
              });
              handleRoleChange(e.value as 'user' | 'admin' | 'skycop');
            }}
            options={userRoleOptions}
          />
        </div>
        <hr />
        <h3>{t('editUserModal.personalInformation.subject')}</h3>
        <div className={styles.editUserInfoSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="firstName">{t('editUserModal.personalInformation.labels.firstName')}</label>
            <CustomTextInput maxLength={25} type="text" id="firstName" disabled={!editMode} value={editUserObject?.firstName} onChange={(e) => setEditUserObject({ ...editUserObject, firstName: e.target.value })} required />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="lastName">{t('editUserModal.personalInformation.labels.lastName')}</label>
            <CustomTextInput maxLength={25} type="text" id="lastName" disabled={!editMode} value={editUserObject?.lastName} onChange={(e) => setEditUserObject({ ...editUserObject, lastName: e.target.value })} required />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="email">{t('editUserModal.personalInformation.labels.email')}</label>
            <CustomTextInput maxLength={25} type="email" id="email" disabled value={editUserObject?.email} />
          </div>

          <div className={styles.inputContainer}>
            <label htmlFor="phoneNumber">{t('editUserModal.personalInformation.labels.phoneNumber')}</label>
            <CustomTextInput maxLength={25} type="text" id="phoneNumber" disabled={!editMode} value={editUserObject?.phoneNumber} onChange={(e) => setEditUserObject({ ...editUserObject, phoneNumber: e.target.value })} required />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="birthDate">{t('editUserModal.personalInformation.labels.birthDate')}</label>
            <CustomTextInput
              type="date"
              id="birthDate"
              disabled={!editMode}
              value={editUserObject?.birthDate ? new Date(editUserObject.birthDate).toISOString().split('T')[0] : ''}
              onChange={(e) => setEditUserObject({ ...editUserObject, birthDate: new Date(e.target.value) })}
              required
            />
          </div>
        </div>
        <hr />
        <h3>{t('editUserModal.residentalInformation.subject')}</h3>
        <div className={styles.editUserInfoSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="address">{t('editUserModal.residentalInformation.labels.address')}</label>
            <CustomTextInput type="text" id="address" disabled={!editMode} value={editUserObject?.address} onChange={(e) => setEditUserObject({ ...editUserObject, address: e.target.value })} required />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="city">{t('editUserModal.residentalInformation.labels.city')}</label>
            <CustomTextInput type="text" id="city" disabled={!editMode} value={editUserObject?.city} onChange={(e) => setEditUserObject({ ...editUserObject, city: e.target.value })} required />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="zipCode">{t('editUserModal.residentalInformation.labels.zipCode')}</label>
            <CustomTextInput type="text" id="zipCode" disabled={!editMode} value={editUserObject?.zipCode} onChange={(e) => setEditUserObject({ ...editUserObject, zipCode: e.target.value })} required />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="country">{t('editUserModal.residentalInformation.labels.countryCode')}</label>
            <CustomTextInput type="text" id="country" disabled={!editMode} value={editUserObject?.country} onChange={(e) => setEditUserObject({ ...editUserObject, country: e.target.value })} required />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="language">{t('editUserModal.residentalInformation.labels.languageCode')}</label>
            <CustomTextInput type="text" id="language" disabled={!editMode} value={editUserObject?.language} onChange={(e) => setEditUserObject({ ...editUserObject, language: e.target.value })} required />
          </div>
        </div>
        <hr />
        <h3>{t('editUserModal.paymentInformation.subject')}</h3>
        <div className={styles.editUserInfoSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="bankCountry">{t('editUserModal.paymentInformation.labels.bankCountry')}</label>
            <CustomTextInput type="text" id="bankCountry" disabled={!editMode} value={editUserObject?.paymentInfo?.bankCountry} onChange={(e) => setEditUserObject({ ...editUserObject, paymentInfo: { ...editUserObject?.paymentInfo, bankCountry: e.target.value } })} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="currency">{t('editUserModal.paymentInformation.labels.currency')}</label>
            <CustomTextInput type="text" id="currency" disabled={!editMode} value={editUserObject?.paymentInfo?.currency} onChange={(e) => setEditUserObject({ ...editUserObject, paymentInfo: { ...editUserObject?.paymentInfo, currency: e.target.value } })} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="iban">{t('editUserModal.paymentInformation.labels.iban')}</label>
            <CustomTextInput type="text" id="iban" disabled={!editMode} value={editUserObject?.paymentInfo?.iban} onChange={(e) => setEditUserObject({ ...editUserObject, paymentInfo: { ...editUserObject?.paymentInfo, iban: e.target.value } })} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="bankTitle">{t('editUserModal.paymentInformation.labels.bankTitle')}</label>
            <CustomTextInput type="text" id="bankTitle" disabled={!editMode} value={editUserObject?.paymentInfo?.bankTitle} onChange={(e) => setEditUserObject({ ...editUserObject, paymentInfo: { ...editUserObject?.paymentInfo, bankTitle: e.target.value } })} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="bankAddress">{t('editUserModal.paymentInformation.labels.bankAddress')}</label>
            <CustomTextInput type="text" id="bankAddress" disabled={!editMode} value={editUserObject?.paymentInfo?.bankAddress} onChange={(e) => setEditUserObject({ ...editUserObject, paymentInfo: { ...editUserObject?.paymentInfo, bankAddress: e.target.value } })} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="payoutFirstName">{t('editUserModal.paymentInformation.labels.payoutFirstName')}</label>
            <CustomTextInput type="text" id="payoutFirstName" disabled={!editMode} value={editUserObject?.paymentInfo?.firstName} onChange={(e) => setEditUserObject({ ...editUserObject, paymentInfo: { ...editUserObject?.paymentInfo, firstName: e.target.value } })} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="payoutLastName">{t('editUserModal.paymentInformation.labels.payoutLastName')}</label>
            <CustomTextInput type="text" id="payoutLastName" disabled={!editMode} value={editUserObject?.paymentInfo?.lastName} onChange={(e) => setEditUserObject({ ...editUserObject, paymentInfo: { ...editUserObject?.paymentInfo, lastName: e.target.value } })} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="payoutCountry">{t('editUserModal.paymentInformation.labels.payoutCountry')}</label>
            <CustomTextInput type="text" id="payoutCountry" disabled={!editMode} value={editUserObject?.paymentInfo?.userCountry} onChange={(e) => setEditUserObject({ ...editUserObject, paymentInfo: { ...editUserObject?.paymentInfo, userCountry: e.target.value } })} />
          </div>
        </div>
        <button type="button" onClick={() => setEditMode(!editMode)} disabled={loading} className={editMode ? 'primaryButton inverse' : 'primaryButton'}>
          {editMode ? t('editUserModal.buttons.cancel') : t('editUserModal.buttons.edit')}
        </button>
        {editMode && (
          <button type="submit" disabled={loading} className="primaryButton">
            {t('editUserModal.buttons.save')}
          </button>
        )}
      </form>
    </div>
  );
};

export default EditUserModal;
